'use client';

import {
  Stack,
  Box,
  Typography,
  MenuItem,
  IconButton,
  Select,
  styled,
} from '@mui/material';
import {
  gridPageCountSelector,
  gridPageSelector,
  gridPageSizeSelector,
  useGridApiContext,
  useGridSelector,
} from '@mui/x-data-grid-pro';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useDatadogTracker } from '@/hooks/studbook/datadog/useDatadogTracker';
import { EventType } from '@/lib/types';
import useStudbookContext from '@/studbook/store/useStudbookContext';
import useSearchStore from '@/studbook/store/useSearchStore';

export const PAGE_SIZE_OPTIONS = [10, 25, 50];

const PaginationSelect = styled(Select)(({ theme }) => ({
  borderRadius: 5,
  color: theme.palette.info.main,
  [`&:hover`]: {
    borderColor: theme.palette.info.main,
  },
  [`& .MuiInputBase-input`]: {
    paddingBlock: 1,
    border: 0,
  },
}));

export default function CustomPagination({ rowCount = 0 }) {
  const apiRef = useGridApiContext();
  const page = useGridSelector(apiRef, gridPageSelector);
  const pageCount = useGridSelector(apiRef, gridPageCountSelector);
  const pageSize = useGridSelector(apiRef, gridPageSizeSelector);

  const { trackEvent } = useDatadogTracker();
  const { orgId } = useStudbookContext();
  const { searchType } = useSearchStore();

  return (
    <Stack direction={'row'} gap={2}>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Typography variant="labelmedium" sx={{ paddingInlineEnd: 2 }}>
          Rows per page:
        </Typography>
        <PaginationSelect
          color="info"
          value={pageSize}
          onChange={(event) =>
            apiRef.current?.setPageSize(Number(event.target.value))
          }
          sx={{
            [`& .MuiInputBase-input`]: {
              paddingBlock: 1,
            },
          }}
        >
          {PAGE_SIZE_OPTIONS.map((option, index) => (
            <MenuItem key={index} value={option}>
              {option}
            </MenuItem>
          ))}
        </PaginationSelect>
      </Box>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Typography variant="labelmedium" sx={{ paddingInlineEnd: 2 }}>
          {pageSize * page + 1}-{Math.min(pageSize * (page + 1), rowCount)} of{' '}
          {rowCount}
        </Typography>
        <Box>
          <IconButton
            color="info"
            size="small"
            disabled={page <= 0}
            onClick={(event) => apiRef.current?.setPage(page - 1)}
          >
            <ArrowBackIcon />
          </IconButton>
          <IconButton
            color="info"
            size="small"
            onClick={(event) => {
              if (page + 1 > 1) {
                trackEvent?.(
                  'SearchPagination',
                  {
                    page: page + 1,
                    orgId: orgId,
                    searchType,
                  },
                  EventType.NAVIGATION_CLICK,
                );
              }
              apiRef.current?.setPage(page + 1);
            }}
            disabled={page >= pageCount - 1}
          >
            <ArrowForwardIcon />
          </IconButton>
        </Box>
      </Box>
    </Stack>
  );
}
